import {Flow, FlowPlan} from "./Flow.js";
import type {BaseFlowStep} from "./steps/BaseFlowStep";
import {isFunction} from "../../../stem-core/base/Utils.js";
import {iFrameState} from "../../services/IFrameState.js";
import {PANEL_TYPE, type PanelType} from "../../../blink-sdk/Constants.js";
import {DonationFlowHandler} from "./DonationFlowHandler.js";
import {SubscriptionFlowHandler} from "./SubscriptionFlowHandler.js";
import {GiftSubscriptionFlowHandler} from "./GiftSubscriptionFlowHandler.js";
import {ShopFlowHandler} from "./ShopFlowHandler.js";
import {DashboardFlowHandler} from "./DashboardFlowHandler.js";
import {Router} from "../../../stem-core/ui/Router.jsx";
import {NewsletterFlowHandler} from "./NewsletterFlowHandler.js";
import {FUNCTIONALITY_NOT_ENABLED_URL} from "../PanelConstants.js";
import {iFrameMerchantService} from "../../services/IframeMerchantService.js";
import {BaseFlowHandler} from "./BaseFlowHandler.js";
import {PaywallPurchaseFlowHandler} from "./PaywallPurchaseFlowHandler.js";
import {PaymentMethodFlowStep} from "./steps/PaymentMethodFlowStep.js";
import {SubscriptionPlanFlowStep} from "./steps/SubscriptionPlanFlowStep.js";
import {AddressFlowStep} from "./steps/AddressFlowStep.js";
import {AuthFlowStep} from "./steps/AuthFlowStep";
import {GiftSubscriptionDetailsFlowStep} from "./steps/GiftSubscriptionDetailsFlowStep.js";
import {SubscriptionReviewFlowStep} from "./steps/SubscriptionReviewFlowStep.js";
import {DonationDetailsFlowStep} from "./steps/DonationDetailsFlowStep.js";
import {PaymentAndDonateFlowStep} from "./steps/PaymentAndDonateFlowStep.js";
import {ProductListFlowStep} from "./steps/ProductListFlowStep.js";
import {PurchaseReviewFlowStep} from "./steps/PurchaseReviewFlowStep.js";
import {EditSubscriptionFlowHandler} from "./EditSubscriptionFlowHandler.js";
import {EditSubscriptionConfirmationStep} from "./steps/EditSubscriptionConfirmationStep.js";
import {CustomPanelFlowHandler} from "./CustomPanelFlowHandler.js";
import {OAuthConnectFlowHandler} from "./OAuthConnectFlowHandler.js";


// What the Object.assign below puts on Flow, plus the two pieces of state a running plan keeps there.
// Flow.ts declares Flow with this through a type-only import, which the build erases, so naming every
// member costs none of the circular imports the Object.assign exists to avoid
export interface FlowMembers {
    auth: AuthFlowStep;
    address: AddressFlowStep;
    paymentMethod: PaymentMethodFlowStep;
    subscriptionPlan: SubscriptionPlanFlowStep;
    giftSubscriptionDetails: GiftSubscriptionDetailsFlowStep;
    subscriptionReview: SubscriptionReviewFlowStep;
    editSubscriptionConfirmation: EditSubscriptionConfirmationStep;
    donationDetails: DonationDetailsFlowStep;
    paymentAndDonationDetails: PaymentAndDonateFlowStep;
    productList: ProductListFlowStep;
    purchaseReview: PurchaseReviewFlowStep;

    donation: DonationFlowHandler;
    subscribe: SubscriptionFlowHandler;
    giftSubscription: GiftSubscriptionFlowHandler;
    editSubscription: EditSubscriptionFlowHandler;
    payment: PaywallPurchaseFlowHandler;
    shop: ShopFlowHandler;
    dashboard: DashboardFlowHandler;
    newsletter: NewsletterFlowHandler;
    oauthConnect: OAuthConnectFlowHandler;
    custom: CustomPanelFlowHandler;

    flowPlan?: FlowPlan;
    // The auth step stands in for a handler on an auth panel, which updateActiveFlow allows explicitly
    activeFlowHandler?: BaseFlowHandler | AuthFlowStep | null;

    startPlan(flowPlan: FlowPlan): void;
    getCurrentStep(): BaseFlowStep;
    promptAtStep(desiredStep: BaseFlowStep): void;
    back(): void;
    renderBackButton(): ReturnType<BaseFlowStep["renderBackButton"]>;
    startPlanFromActiveHandler(): void;
    clearActiveFlow(): void;
    isFlowTypeEnabled(panelType: string): boolean;
    updateActiveFlow(panelType?: PanelType): void;
}

// The methods below are copied onto Flow, so inside them this is that object
type FlowThis = FlowMembers;

// We're overwriting the object here to prevent circular imports
Object.assign(Flow, {
    // Used as inputs from other main flows
    auth: new AuthFlowStep(),
    address: new AddressFlowStep(),
    paymentMethod: new PaymentMethodFlowStep(),
    subscriptionPlan: new SubscriptionPlanFlowStep(),
    giftSubscriptionDetails: new GiftSubscriptionDetailsFlowStep(),
    subscriptionReview: new SubscriptionReviewFlowStep(),
    editSubscriptionConfirmation: new EditSubscriptionConfirmationStep(),
    donationDetails: new DonationDetailsFlowStep(),
    paymentAndDonationDetails: new PaymentAndDonateFlowStep(),
    productList: new ProductListFlowStep(),
    purchaseReview: new PurchaseReviewFlowStep(),

    // Linked directly to panels
    donation: new DonationFlowHandler(),
    subscribe: new SubscriptionFlowHandler(),
    giftSubscription: new GiftSubscriptionFlowHandler(),
    editSubscription: new EditSubscriptionFlowHandler(),
    payment: new PaywallPurchaseFlowHandler(),
    shop: new ShopFlowHandler(),
    dashboard: new DashboardFlowHandler(),
    newsletter: new NewsletterFlowHandler(),
    oauthConnect: new OAuthConnectFlowHandler(),
    custom: new CustomPanelFlowHandler(),

    startPlan(this: FlowThis, flowPlan: FlowPlan) {
        this.flowPlan = flowPlan;
        this.flowPlan.prompt();
    },

    getCurrentStep(this: FlowThis) {
        return this.flowPlan.getCurrentStep();
    },

    // Move to that step in the flow. Will force show, even if the step wants to hide itself.
    promptAtStep(this: FlowThis, desiredStep: BaseFlowStep) {
        this.flowPlan.goToStep(desiredStep);
        this.flowPlan.prompt(true);
    },

    back(this: FlowThis) {
        // TODO Router.back() synchronously triggers a render cycle, which can call renderBackButton()
        // while flowPlan is null. Consider moving Router.back() after promptPreviousStep(), or adding
        // a null guard in getCurrentStep()/renderBackButton().
        Router.back();
        this.flowPlan?.promptPreviousStep().then(() => {
            if (this.flowPlan?.isBeforeFirstStep()) {
                this.activeFlowHandler = null; // We want back and exited the flow
            }
        });
        // TODO @flow2 Send a message that we might need to recalculate the panel content
    },

    renderBackButton(this: FlowThis) {
        return this.getCurrentStep().renderBackButton();
    },

    startPlanFromActiveHandler(this: FlowThis) {
        // Annotated for the branch below: getFlowPlan is declared to answer with the steps, and the
        // instanceof check turns them into a plan
        let flowPlan: FlowPlan | any[] = this.activeFlowHandler.getFlowPlan();

        if (!flowPlan) {
            // We've probably changed to another panel
            // TODO @flow2 this is here only to support very simple unmigrated stuff
            return;
        }

        if (!(flowPlan instanceof FlowPlan)) {
            flowPlan = new FlowPlan(flowPlan);
        }

        for (const step of flowPlan.steps) {
            // Just here so we don't need to manually init all steps manually when making the plan
            if (step.flowHandler !== this.activeFlowHandler) {
                step.init({});
            }
        }

        this.startPlan(flowPlan);
    },

    clearActiveFlow(this: FlowThis) {
        this.activeFlowHandler = null;
        delete this.flowPlan;

        // TODO @flow2 temp cludge to ensure we have all flows
        for (const [key, value] of Object.entries(this)) {
            if (value === Flow.paymentMethod || value === Flow.address) {
                continue;
            }
            
            if (!value || isFunction(value)) {
                // Just a regular method
                continue;
            }

            const Cls = value.constructor as new () => any;
            // Re-creates whichever member the loop just read under this key
            (this as unknown as Record<string, unknown>)[key] = new Cls();
        }
    },

    isFlowTypeEnabled(this: FlowThis, panelType: string) {
        // TODO @flow move inside merchant?
        const merchant = iFrameMerchantService.getMerchant();
        const FLOW_TYPE_ENABLED = {
            [PANEL_TYPE.auth]: true,
            [PANEL_TYPE.custom]: true,
            [PANEL_TYPE.dashboard]: true,
            [PANEL_TYPE.oauthConnect]: true, // Sure, why not?
            [PANEL_TYPE.payment]: merchant.allowPurchases,
            [PANEL_TYPE.shop]: merchant.allowPurchases,
            [PANEL_TYPE.donation]: merchant.allowDonations,
            [PANEL_TYPE.subscribe]: merchant.allowSubscriptions,
            [PANEL_TYPE.giftSubscription]: merchant.allowSubscriptions,
            [PANEL_TYPE.newsletter]: merchant.allowEmailing,
        };
        return FLOW_TYPE_ENABLED[panelType];
    },

    updateActiveFlow(this: FlowThis, panelType = iFrameState.panelType) {
        if (panelType === PANEL_TYPE.empty || this.activeFlowHandler?.sameAsNewOptions(panelType)) {
            return;
        }

        this.clearActiveFlow();

        if (!this.isFlowTypeEnabled(panelType)) {
            Router.changeURL(FUNCTIONALITY_NOT_ENABLED_URL);
            return;
        }

        if (!iFrameState.handleFlowInline()) {
            Router.changeToCustomPanel();
            return;
        }

        // TODO @flow2 It might make more sense to consider a stack of flows?
        this.activeFlowHandler = this[panelType];

        if (!(this.activeFlowHandler instanceof BaseFlowHandler) && this.activeFlowHandler !== this.auth) {
            // TODO @flow2 We'll just show the custom panel for now
            this.activeFlowHandler = this.custom;
        }

        this.activeFlowHandler.saveInitialOptions();

        this.startPlanFromActiveHandler();
    },
});
